import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Avatar, Box, Chip, TableBody, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { find, flatten, get, last, map, sum, sumBy } from 'lodash-es';
import React, { useMemo } from 'react';
import { formatNumber } from '~/components/NumberText';
import { DAYS_PER_YEAR } from '~/constants/date';
import { formatUiDate } from '~/lib/date';
import { calculateDailyEnergy } from '~/models/common';
import { getMeterContractEffectiveEndDate } from '~/models/meter';
import { EnergyPricingOfferTypes } from '~/models/project';
import { calculateWeightedRatesByEnergyFromSegments } from '../weightedRateUtils';
import { calculateMeterConsumption, calculateMeterConsumptionByComponent } from './meterOfferCalculationUtils';
import { CalculatedProjectOfferPeriodsByName, EAReportGroupedMeter } from './reportTypes';
import {
  ChipMarginBottom,
  HighlightCellStyle,
  NoPageBreakBefore,
  NoPageBreakInside,
  PageBreakBefore,
  Section,
  SectionContainer,
  StyledTable,
  StyledTableCell,
  StyledTableRow,
  Title,
  TitleUnderline,
  TotalLabelCellStyle,
  TotalRowStyle,
  TotalValueCellStyle
} from './style';

const SummarySection: React.FC<{ groupedMeters: EAReportGroupedMeter[] }> = ({ groupedMeters }) => {
  let isStepped = false;
  const groupedMetersWithBestOffer = map(groupedMeters, (groupedMeter) => {
    const bestOffer = find(groupedMeter.offers, (offer) => offer.isBest);
    const meterPeriods = groupedMeter.meterPeriods;
    const bestOfferPeriod = (() => {
      if (meterPeriods && groupedMeter.meters.length > 0) {
        const first = meterPeriods[groupedMeter.meters[0].id];
        if (first?.periodName) {
          isStepped = first.offerType === EnergyPricingOfferTypes.STEPPED;
          return first;
        }
      }
      const period = find(bestOffer?.periods, (period) => period.isBest) || ({} as CalculatedProjectOfferPeriodsByName);
      isStepped = period.offerType === EnergyPricingOfferTypes.STEPPED;
      return period;
    })();
    return {
      ...groupedMeter,
      bestOffer,
      bestOfferPeriod
    };
  });

  // Check if any grouped meter has current contract data
  const hasCurrentContract = groupedMetersWithBestOffer.some((groupedMeter) => groupedMeter.currentContestableCost > 0);

  // Pre-compute per-meter Future annual values (for Total row) - same logic as meter rows
  const futureMeterAnnualValues = useMemo(() => {
    const result: {
      energyCostAnnual: number;
      envRetailerAnnual: number;
      contestableAnnual: number;
      networkRegulatedAnnual: number;
      totalNewAnnual: number;
    }[] = [];
    groupedMetersWithBestOffer.forEach((group) => {
      const { meters, bestOfferPeriod, meterPeriods, meterOfferPeriodMapping, offers: groupOffers } = group;
      const environmentalRatePeriods = bestOfferPeriod?.environmentalRatePeriods || [];
      if (!bestOfferPeriod) return;
      meters.forEach((meter) => {
        const meterRecommendedPeriod =
          meterPeriods?.[meter.id] ??
          (() => {
            const recId = meterOfferPeriodMapping?.[meter.id]?.recommendedPeriodId;
            if (recId && groupOffers?.length) {
              const found = flatten((groupOffers || []).map((o) => o.periods || [])).find(
                (p: { id?: string }) => p.id === recId
              );
              if (found) return found as CalculatedProjectOfferPeriodsByName;
            }
            return bestOfferPeriod;
          })();
        const totalNumOfDays =
          meterRecommendedPeriod.totalNumOfDays ??
          sumBy(meterRecommendedPeriod.environmentalRatePeriods ?? [], (s) => s.numOfDays ?? 0) ??
          0;
        const divide = totalNumOfDays > 0 ? DAYS_PER_YEAR / totalNumOfDays : 1;
        let energyCost = meterRecommendedPeriod.meterEnergyCosts?.[meter.id] ?? 0;
        let renewableCost = meterRecommendedPeriod.meterRenewableCosts?.[meter.id] ?? 0;
        let retailerFee = meterRecommendedPeriod.meterRetailerServiceFee?.[meter.id] ?? 0;
        let meteringCharge = meterRecommendedPeriod.meterMeteringCharge?.[meter.id] ?? 0;
        let contestableCost = meterRecommendedPeriod.meterContestableCosts?.[meter.id] ?? 0;
        if (contestableCost === 0) {
          energyCost = 0;
          renewableCost = 0;
          retailerFee = 0;
          meteringCharge = 0;
          const meterEnvironmentalRatePeriods =
            meterRecommendedPeriod.environmentalRatePeriods || environmentalRatePeriods;
          const offerForPeriod = groupOffers?.find((o) =>
            (o.periods || []).some((p: { id?: string }) => p.id === meterRecommendedPeriod.id)
          );
          meterEnvironmentalRatePeriods.forEach((segment) => {
            const comp = calculateMeterConsumptionByComponent(meter, segment.numOfDays ?? 0, {
              usePerMeterLossFactor: true
            });
            const totalConsumptionWithLoss = comp.peak + comp.offPeak + comp.shoulder;
            energyCost +=
              (comp.peak * (segment.peak ?? 0) +
                comp.offPeak * (segment.offPeak ?? 0) +
                comp.shoulder * (segment.shoulder ?? 0)) /
              100;
            renewableCost +=
              (sum([
                segment.lret ?? 0,
                segment.sres ?? 0,
                segment.ess ?? 0,
                segment.veet ?? 0,
                segment.aeeis ?? 0,
                segment.pdrs ?? 0,
                segment.reps ?? 0
              ]) *
                totalConsumptionWithLoss) /
              100;
            const annualRetailerFeePerMeter = meter.annualData?.retailerFeePerMeter ?? 0;
            const annualMeteringChargePerMeter = meter.annualData?.meteringChargePerMeter ?? 0;
            retailerFee +=
              offerForPeriod?.serviceFeePrice != null
                ? offerForPeriod.dailyServiceFee * segment.numOfDays * meter.numOfMeters
                : ((annualRetailerFeePerMeter * segment.numOfDays) / DAYS_PER_YEAR) * meter.numOfMeters;
            meteringCharge +=
              offerForPeriod?.meteringFeePrice != null
                ? offerForPeriod.dailyMeteringFee * segment.numOfDays * meter.numOfMeters
                : ((annualMeteringChargePerMeter * segment.numOfDays) / DAYS_PER_YEAR) * meter.numOfMeters;
          });
          contestableCost = sum([energyCost, renewableCost, meteringCharge, retailerFee]);
        }
        const meterConsumptionForPeriod = calculateMeterConsumption(meter, totalNumOfDays, {
          usePerMeterLossFactor: true
        });
        const networkCharge =
          (sum([
            meter.annualData?.networkStanding ?? 0,
            meter.annualData?.networkChargesEnergy ?? 0,
            meter.annualData?.networkChargesDemand ?? 0
          ]) /
            DAYS_PER_YEAR) *
          totalNumOfDays;
        const regulatedCost =
          (sum([meter.ancillaryCharge ?? 0, meter.participantCharge ?? 0]) * meterConsumptionForPeriod) / 100;
        result.push({
          energyCostAnnual: energyCost * divide,
          envRetailerAnnual: (renewableCost + retailerFee + meteringCharge) * divide,
          contestableAnnual: contestableCost * divide,
          networkRegulatedAnnual: (networkCharge + regulatedCost) * divide,
          totalNewAnnual:
            (energyCost + renewableCost + retailerFee + meteringCharge + networkCharge + regulatedCost) * divide
        });
      });
    });
    return result;
  }, [groupedMetersWithBestOffer]);

  const totalEnergyCostAnnual = sumBy(futureMeterAnnualValues, 'energyCostAnnual');
  const totalEnvRetailerAnnual = sumBy(futureMeterAnnualValues, 'envRetailerAnnual');
  const totalContestableAnnual = sumBy(futureMeterAnnualValues, 'contestableAnnual');
  const totalNetworkRegulatedAnnual = sumBy(futureMeterAnnualValues, 'networkRegulatedAnnual');
  const totalNewExpenditureAnnual = sumBy(futureMeterAnnualValues, 'totalNewAnnual');
  const totalCurrentExpenditure = sumBy(groupedMeters, ({ meters }) =>
    sumBy(meters, (meter) =>
      sum([
        meter.annualData?.contestableCost ?? 0,
        meter.annualData?.networkStanding ?? 0,
        meter.annualData?.networkChargesEnergy ?? 0,
        meter.annualData?.networkChargesDemand ?? 0,
        meter.annualData?.regulatedCost ?? 0
      ])
    )
  );

  return (
    <>
      <Section id="summary" sx={{ ...NoPageBreakInside, width: '100%' }}>
        <SectionContainer sx={{ textAlign: 'center' }}>
          <Title variant="h4">Summary</Title>
          <TitleUnderline />
          <Typography variant="h5">All Contracts / Terms</Typography>
        </SectionContainer>
      </Section>

      <Section>
        <SectionContainer>
          <Typography variant="h6" mb={1} sx={NoPageBreakBefore}>
            Current
          </Typography>
          <TableContainer sx={NoPageBreakBefore}>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <StyledTableCell>
                    State
                    <br />
                    /Group
                  </StyledTableCell>
                  <StyledTableCell>NMI</StyledTableCell>
                  <StyledTableCell>Suburb</StyledTableCell>
                  <StyledTableCell>Current Retailer</StyledTableCell>
                  <StyledTableCell>Contract End Date</StyledTableCell>
                  <StyledTableCell>Total kWh p.a</StyledTableCell>
                  <StyledTableCell>Current Rate c/kWh</StyledTableCell>
                  <StyledTableCell>Commodity Total (Annual)</StyledTableCell>
                  <StyledTableCell>Environmental & Retailer (Annual)</StyledTableCell>
                  <StyledTableCell>Current Contestable (Annual)</StyledTableCell>
                  <StyledTableCell>Network & Regulated Charges (Annual)</StyledTableCell>
                  <StyledTableCell>Total Current Expenditure (Annual)</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedMeters.map(({ meters, name }, index) => {
                  return (
                    <React.Fragment key={`site-${meters[0].state}-${index}`}>
                      {map(meters, (meter, meterIndex) => {
                        return (
                          <StyledTableRow key={meter.meterNumber}>
                            {meterIndex === 0 && (
                              <StyledTableCell rowSpan={meters.length}>{name || meter.state}</StyledTableCell>
                            )}
                            <StyledTableCell>
                              <Typography variant="body2">{meter.meterNumber ?? '-'}</Typography>
                            </StyledTableCell>
                            <StyledTableCell>{meter.suburb}</StyledTableCell>
                            <StyledTableCell>{get(meter, 'contract.retailer.name')}</StyledTableCell>
                            <StyledTableCell>
                              {formatUiDate(getMeterContractEffectiveEndDate(meter.energyType, get(meter, 'contract')))}
                            </StyledTableCell>
                            <StyledTableCell sx={HighlightCellStyle}>
                              {formatNumber(
                                calculateMeterConsumption(meter, DAYS_PER_YEAR, { usePerMeterLossFactor: false }),
                                0,
                                '-'
                              )}
                            </StyledTableCell>
                            <StyledTableCell>
                              <Chip
                                sx={ChipMarginBottom}
                                size="small"
                                avatar={<Avatar>P</Avatar>}
                                label={formatNumber(meter.contractPeriod.peakRate, 4, '-')}
                              />
                              <br />
                              <Chip
                                sx={ChipMarginBottom}
                                size="small"
                                avatar={<Avatar>OP</Avatar>}
                                label={formatNumber(meter.contractPeriod.offPeakRate, 4, '-')}
                              />
                              <br />
                              <Chip
                                size="small"
                                avatar={<Avatar>S</Avatar>}
                                label={formatNumber(meter.contractPeriod.shoulderRate, 4, '-')}
                              />
                            </StyledTableCell>
                            <StyledTableCell>${formatNumber(meter.annualData.energyCost, 0, '-')}</StyledTableCell>
                            <StyledTableCell>
                              $
                              {formatNumber(
                                sum([
                                  meter.annualData.renewableCost,
                                  meter.annualData.annualRetailerFee,
                                  meter.annualData.annualMeteringCharge
                                ]),
                                0,
                                '-'
                              )}
                            </StyledTableCell>
                            <StyledTableCell>${formatNumber(meter.annualData.contestableCost, 0, '-')}</StyledTableCell>
                            <StyledTableCell>
                              $
                              {formatNumber(
                                sum([
                                  meter.annualData.networkStanding,
                                  meter.annualData.networkChargesEnergy,
                                  meter.annualData.networkChargesDemand,
                                  meter.annualData.regulatedCost
                                ]),
                                0,
                                '-'
                              )}
                            </StyledTableCell>
                            <StyledTableCell>
                              $
                              {formatNumber(
                                sum([
                                  meter.annualData.contestableCost,
                                  meter.annualData.regulatedCost,
                                  meter.annualData.networkStanding,
                                  meter.annualData.networkChargesEnergy,
                                  meter.annualData.networkChargesDemand
                                ]),
                                0,
                                '-'
                              )}
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}

                <TableRow sx={TotalRowStyle}>
                  <StyledTableCell colSpan={4}></StyledTableCell>
                  <StyledTableCell sx={TotalLabelCellStyle}>Total kWh</StyledTableCell>
                  <StyledTableCell sx={TotalValueCellStyle}>
                    {formatNumber(
                      sum(
                        flatten(map(groupedMeters, 'meters')).map((m) =>
                          calculateMeterConsumption(m, DAYS_PER_YEAR, { usePerMeterLossFactor: false })
                        )
                      ),
                      0,
                      '-'
                    )}
                  </StyledTableCell>
                  <StyledTableCell sx={TotalLabelCellStyle}>Total</StyledTableCell>
                  <StyledTableCell sx={TotalValueCellStyle}>
                    $
                    {formatNumber(
                      sumBy(groupedMeters, ({ meters }) => sumBy(meters, (item) => sum([item.annualData.energyCost]))),
                      0,
                      '-'
                    )}
                  </StyledTableCell>
                  <StyledTableCell sx={TotalValueCellStyle}>
                    $
                    {formatNumber(
                      sumBy(groupedMeters, ({ meters }) =>
                        sumBy(meters, (item) =>
                          sum([
                            item.annualData.renewableCost,
                            item.annualData.annualRetailerFee,
                            item.annualData.annualMeteringCharge
                          ])
                        )
                      ),
                      0,
                      '-'
                    )}
                  </StyledTableCell>
                  <StyledTableCell sx={TotalValueCellStyle}>
                    $
                    {formatNumber(
                      sumBy(groupedMeters, ({ meters }) =>
                        sumBy(meters, (item) => sum([item.annualData.contestableCost]))
                      ),
                      0,
                      '-'
                    )}
                  </StyledTableCell>
                  <StyledTableCell sx={TotalValueCellStyle}>
                    $
                    {formatNumber(
                      sumBy(groupedMeters, ({ meters }) =>
                        sumBy(meters, (item) =>
                          sum([
                            item.annualData.networkStanding,
                            item.annualData.networkChargesEnergy,
                            item.annualData.networkChargesDemand,
                            item.annualData.regulatedCost
                          ])
                        )
                      ),
                      0,
                      '-'
                    )}
                  </StyledTableCell>
                  <StyledTableCell sx={TotalValueCellStyle}>
                    $
                    {formatNumber(
                      sumBy(groupedMeters, ({ meters }) =>
                        sumBy(meters, (item) =>
                          sum([
                            item.annualData.contestableCost,
                            item.annualData.networkStanding,
                            item.annualData.networkChargesEnergy,
                            item.annualData.networkChargesDemand,
                            item.annualData.regulatedCost
                          ])
                        )
                      ),
                      0,
                      '-'
                    )}
                  </StyledTableCell>
                </TableRow>
              </TableBody>
            </StyledTable>
          </TableContainer>

          <br />

          <Box mb={1} sx={{ ...PageBreakBefore, ...NoPageBreakInside }}>
            <Typography variant="h6">Future</Typography>

            {isStepped && (
              <Typography variant="body2" color="textSecondary">
                (The presented stepped rate is the weighted average rate over the recommended period.)
              </Typography>
            )}
          </Box>

          <TableContainer sx={NoPageBreakBefore}>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <StyledTableCell>
                    State
                    <br />
                    /Group
                  </StyledTableCell>
                  <StyledTableCell>NMI</StyledTableCell>
                  <StyledTableCell>Suburb</StyledTableCell>
                  <StyledTableCell>New Retailer</StyledTableCell>
                  <StyledTableCell>New Term</StyledTableCell>
                  <StyledTableCell>New Rate c/kWh</StyledTableCell>
                  <StyledTableCell>Commodity Total (Annual)</StyledTableCell>
                  <StyledTableCell>Environmental & Retailer (Annual)</StyledTableCell>
                  <StyledTableCell>New Contestable (Annual)</StyledTableCell>
                  <StyledTableCell>Network & Regulated Charges (Annual)</StyledTableCell>
                  <StyledTableCell>Total New Expenditure (Annual)</StyledTableCell>
                  {hasCurrentContract && (
                    <>
                      <StyledTableCell>$ Change (Annual)</StyledTableCell>
                      <StyledTableCell>% Change (Annual)</StyledTableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedMetersWithBestOffer.map(
                  (
                    {
                      meters,
                      lwaDLF: _lwaDLF,
                      lwaMLF: _lwaMLF,
                      bestOffer,
                      bestOfferPeriod,
                      name,
                      termSelections,
                      offers: groupOffers,
                      meterPeriods,
                      meterOfferPeriodMapping
                    },
                    index
                  ) => {
                    // Guard against missing bestOfferPeriod (same as old code)
                    // Allow display even if environmentalRatePeriods is missing/empty (old code didn't check this)
                    if (!bestOfferPeriod) {
                      return null;
                    }
                    // Use environmentalRatePeriods if available, otherwise fallback to empty array for calculations
                    const environmentalRatePeriods = bestOfferPeriod.environmentalRatePeriods || [];
                    if (environmentalRatePeriods.length === 0) {
                      // Still render the row but show empty/missing data indicators
                    }
                    // divide is computed per meter from meterRecommendedPeriod.periodName (see below)

                    // Find selected term from termSelections to support Mix & Match
                    const selectedTermSelection = termSelections?.find((ts) => ts.selected);
                    const selectedTermName = selectedTermSelection?.periodName || bestOfferPeriod.periodName;

                    return (
                      <React.Fragment key={`site-${meters[0].state}-${index}`}>
                        {map(meters, (meter, meterIndex) => {
                          // Get meter's selected retailer from termSelections if available (supports Mix & Match)
                          // This ensures EA Report uses the same retailer selection as Term Dialog
                          let meterContestableCost = 0;
                          let meterSelectedRetailerId: string | null = null;
                          // Per-meter period when available (merged groups: each meter has its own offer/period)
                          let meterSelectedPeriod = meterPeriods?.[meter.id] ?? bestOfferPeriod;

                          if (selectedTermSelection) {
                            // Find this meter's selected retailer in the selected term
                            const meterSelection = selectedTermSelection.meters.find((m) => m.meterId === meter.id);
                            if (meterSelection) {
                              meterSelectedRetailerId = meterSelection.selectedRetailerId;
                              // Find the offer with this retailer and the selected term period
                              const foundOffer = groupOffers.find(
                                (offer) => offer.retailer?.id === meterSelectedRetailerId
                              );
                              if (foundOffer) {
                                // Find the period matching the selected term name
                                const foundPeriod = foundOffer.periods.find((p) => p.periodName === selectedTermName);
                                if (foundPeriod) {
                                  meterSelectedPeriod = foundPeriod;
                                  // Get contestableCost from the meter's selected offer/period
                                  if (foundPeriod.meterContestableCosts) {
                                    meterContestableCost = foundPeriod.meterContestableCosts[meter.id] ?? 0;
                                  }
                                }
                              }
                            }
                          }

                          // Fallback contestable cost from meter's period or bestOfferPeriod
                          if (meterContestableCost === 0 && !meterSelectedRetailerId) {
                            meterContestableCost =
                              meterSelectedPeriod.meterContestableCosts?.[meter.id] ??
                              bestOfferPeriod.meterContestableCosts?.[meter.id] ??
                              0;
                          }

                          // New Rate c/kWh + costs: always use each meter's recommended period
                          const meterRecommendedPeriod =
                            meterPeriods?.[meter.id] ??
                            (() => {
                              const recId = meterOfferPeriodMapping?.[meter.id]?.recommendedPeriodId;
                              if (recId && groupOffers?.length) {
                                const found = flatten((groupOffers || []).map((o) => o.periods || [])).find(
                                  (p: { id?: string }) => p.id === recId
                                );
                                if (found) return found as CalculatedProjectOfferPeriodsByName;
                              }
                              return bestOfferPeriod;
                            })();

                          const periodName = meterRecommendedPeriod.periodName || bestOfferPeriod.periodName;
                          const totalNumOfDays =
                            meterRecommendedPeriod.totalNumOfDays ??
                            sumBy(meterRecommendedPeriod.environmentalRatePeriods ?? [], (s) => s.numOfDays ?? 0) ??
                            0;
                          const divide = totalNumOfDays > 0 ? DAYS_PER_YEAR / totalNumOfDays : 1;

                          // 1. First: get total for the period from stored values (offer calculation pipeline)
                          let energyCost = meterRecommendedPeriod.meterEnergyCosts?.[meter.id] ?? 0;
                          let renewableCost = meterRecommendedPeriod.meterRenewableCosts?.[meter.id] ?? 0;
                          let retailerFee = meterRecommendedPeriod.meterRetailerServiceFee?.[meter.id] ?? 0;
                          let meteringCharge = meterRecommendedPeriod.meterMeteringCharge?.[meter.id] ?? 0;
                          let contestableCost = meterRecommendedPeriod.meterContestableCosts?.[meter.id] ?? 0;

                          if (contestableCost === 0) {
                            energyCost = 0;
                            renewableCost = 0;
                            retailerFee = 0;
                            meteringCharge = 0;
                            const meterEnvironmentalRatePeriods =
                              meterRecommendedPeriod.environmentalRatePeriods || environmentalRatePeriods;
                            const offerForPeriod = groupOffers?.find((o) =>
                              (o.periods || []).some((p: { id?: string }) => p.id === meterRecommendedPeriod.id)
                            );
                            meterEnvironmentalRatePeriods.forEach((segment) => {
                              const comp = calculateMeterConsumptionByComponent(meter, segment.numOfDays ?? 0, {
                                usePerMeterLossFactor: true
                              });
                              const totalConsumptionWithLoss = comp.peak + comp.offPeak + comp.shoulder;
                              energyCost +=
                                (comp.peak * (segment.peak ?? 0) +
                                  comp.offPeak * (segment.offPeak ?? 0) +
                                  comp.shoulder * (segment.shoulder ?? 0)) /
                                100;
                              renewableCost +=
                                (sum([
                                  segment.lret ?? 0,
                                  segment.sres ?? 0,
                                  segment.ess ?? 0,
                                  segment.veet ?? 0,
                                  segment.aeeis ?? 0,
                                  segment.pdrs ?? 0,
                                  segment.reps ?? 0
                                ]) *
                                  totalConsumptionWithLoss) /
                                100;
                              const annualRetailerFeePerMeter = meter.annualData.retailerFeePerMeter ?? 0;
                              const annualMeteringChargePerMeter = meter.annualData.meteringChargePerMeter ?? 0;
                              retailerFee +=
                                offerForPeriod?.serviceFeePrice != null
                                  ? offerForPeriod.dailyServiceFee * segment.numOfDays * meter.numOfMeters
                                  : ((annualRetailerFeePerMeter * segment.numOfDays) / DAYS_PER_YEAR) *
                                    meter.numOfMeters;
                              meteringCharge +=
                                offerForPeriod?.meteringFeePrice != null
                                  ? offerForPeriod.dailyMeteringFee * segment.numOfDays * meter.numOfMeters
                                  : ((annualMeteringChargePerMeter * segment.numOfDays) / DAYS_PER_YEAR) *
                                    meter.numOfMeters;
                            });
                            contestableCost = sum([energyCost, renewableCost, meteringCharge, retailerFee]);
                          }

                          const meterConsumptionForPeriod = calculateMeterConsumption(meter, totalNumOfDays, {
                            usePerMeterLossFactor: true
                          });
                          const networkCharge =
                            (sum([
                              meter.annualData?.networkStanding ?? 0,
                              meter.annualData?.networkChargesEnergy ?? 0,
                              meter.annualData?.networkChargesDemand ?? 0
                            ]) /
                              DAYS_PER_YEAR) *
                            totalNumOfDays;
                          const regulatedCost =
                            (sum([meter.ancillaryCharge ?? 0, meter.participantCharge ?? 0]) *
                              meterConsumptionForPeriod) /
                            100;

                          // 2. Then: annual = period total × (DAYS_PER_YEAR / totalNumOfDays)
                          const meterEnvironmentalRatePeriods =
                            meterRecommendedPeriod.environmentalRatePeriods || environmentalRatePeriods;
                          return (
                            <StyledTableRow key={meter.meterNumber}>
                              {meterIndex === 0 && (
                                <StyledTableCell rowSpan={meters.length}>{name || meter.state}</StyledTableCell>
                              )}
                              <StyledTableCell>
                                <Typography variant="body2">{meter.meterNumber ?? '-'}</Typography>
                              </StyledTableCell>
                              <StyledTableCell>{meter.suburb}</StyledTableCell>
                              <StyledTableCell>
                                {meterSelectedRetailerId
                                  ? groupOffers.find((o) => o.retailer?.id === meterSelectedRetailerId)?.retailer.name
                                  : groupOffers.find((o) =>
                                      (o.periods || []).some((p: { id?: string }) => p.id === meterRecommendedPeriod.id)
                                    )?.retailer?.name ?? bestOffer?.retailer?.name}
                              </StyledTableCell>
                              <StyledTableCell>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                  <Box>
                                    {periodName} <br />
                                    {meterEnvironmentalRatePeriods.length > 0 ? (
                                      <Typography variant="caption" fontStyle="italic" lineHeight="0.8">
                                        ({formatUiDate(meterEnvironmentalRatePeriods[0]?.startDate)}
                                        <br />-<br />
                                        {formatUiDate(last(meterEnvironmentalRatePeriods)?.endDate)})
                                      </Typography>
                                    ) : (
                                      <Typography
                                        variant="caption"
                                        fontStyle="italic"
                                        lineHeight="0.8"
                                        color="text.secondary"
                                      >
                                        (Date range not available)
                                      </Typography>
                                    )}
                                  </Box>
                                  {meterEnvironmentalRatePeriods.length > 0 && (
                                    <Tooltip
                                      title={
                                        <Box sx={{ p: 1, maxHeight: '400px', overflowY: 'auto' }}>
                                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                            Segments Details ({meterEnvironmentalRatePeriods.length} segments)
                                          </Typography>
                                          {meterEnvironmentalRatePeriods.map((segment, idx) => (
                                            <Box
                                              key={idx}
                                              sx={{
                                                mb: 1.5,
                                                pb: 1.5,
                                                borderBottom:
                                                  idx < meterEnvironmentalRatePeriods.length - 1
                                                    ? '1px solid rgba(255,255,255,0.2)'
                                                    : 'none'
                                              }}
                                            >
                                              <Typography
                                                variant="caption"
                                                sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                                              >
                                                Segment {idx + 1}: {formatUiDate(segment.startDate)} -{' '}
                                                {formatUiDate(segment.endDate)} ({segment.numOfDays} days)
                                              </Typography>
                                              <Box sx={{ pl: 1, fontSize: '0.75rem' }}>
                                                <Typography variant="caption" sx={{ display: 'block' }}>
                                                  <strong>Contract Rates (c/kWh):</strong>
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: 'block', pl: 1 }}>
                                                  Peak: {formatNumber(segment.peak ?? 0, 4, '-')}, Off-Peak:{' '}
                                                  {formatNumber(segment.offPeak ?? 0, 4, '-')}, Shoulder:{' '}
                                                  {formatNumber(segment.shoulder ?? 0, 4, '-')}
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                                  <strong>Environmental Rates (c/kWh):</strong>
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: 'block', pl: 1 }}>
                                                  LRET: {formatNumber(segment.lret ?? 0, 4, '-')}, SRES:{' '}
                                                  {formatNumber(segment.sres ?? 0, 4, '-')}, ESS:{' '}
                                                  {formatNumber(segment.ess ?? 0, 4, '-')}, VEET:{' '}
                                                  {formatNumber(segment.veet ?? 0, 4, '-')}, AEEIS:{' '}
                                                  {formatNumber(segment.aeeis ?? 0, 4, '-')}, PDRS:{' '}
                                                  {formatNumber(segment.pdrs ?? 0, 4, '-')}, REPS:{' '}
                                                  {formatNumber(segment.reps ?? 0, 4, '-')}
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                                  <strong>Consumption kWh:</strong>
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: 'block', pl: 1 }}>
                                                  Peak:{' '}
                                                  {formatNumber(
                                                    calculateMeterConsumptionByComponent(
                                                      meter,
                                                      segment.numOfDays ?? 0,
                                                      {
                                                        usePerMeterLossFactor: false
                                                      }
                                                    ).peak,
                                                    0,
                                                    '-'
                                                  )}
                                                  , Off-Peak:{' '}
                                                  {formatNumber(
                                                    calculateMeterConsumptionByComponent(
                                                      meter,
                                                      segment.numOfDays ?? 0,
                                                      {
                                                        usePerMeterLossFactor: false
                                                      }
                                                    ).offPeak,
                                                    0,
                                                    '-'
                                                  )}
                                                  , Shoulder:{' '}
                                                  {formatNumber(
                                                    calculateMeterConsumptionByComponent(
                                                      meter,
                                                      segment.numOfDays ?? 0,
                                                      {
                                                        usePerMeterLossFactor: false
                                                      }
                                                    ).shoulder,
                                                    0,
                                                    '-'
                                                  )}
                                                  , Total:{' '}
                                                  {formatNumber(
                                                    calculateMeterConsumptionByComponent(
                                                      meter,
                                                      segment.numOfDays ?? 0,
                                                      {
                                                        usePerMeterLossFactor: false
                                                      }
                                                    ).total,
                                                    0,
                                                    '-'
                                                  )}
                                                </Typography>
                                              </Box>
                                            </Box>
                                          ))}
                                        </Box>
                                      }
                                      arrow
                                      placement="right"
                                    >
                                      <HelpOutlineIcon
                                        sx={{ fontSize: '0.875rem', color: 'text.secondary', cursor: 'help', mt: 0.25 }}
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </StyledTableCell>
                              <StyledTableCell>
                                {(() => {
                                  const refP = meterRecommendedPeriod ?? bestOfferPeriod;
                                  const segments = refP?.environmentalRatePeriods ?? [];
                                  const dailyPeak = calculateDailyEnergy(meter.annualData?.peakEnergy ?? 0);
                                  const dailyOffPeak = calculateDailyEnergy(meter.annualData?.offPeakEnergy ?? 0);
                                  const dailyShoulder = calculateDailyEnergy(meter.annualData?.shoulderEnergy ?? 0);
                                  const {
                                    peak: weightedPeakRate,
                                    offPeak: weightedOffPeakRate,
                                    shoulder: weightedShoulderRate
                                  } = segments.length > 0
                                    ? calculateWeightedRatesByEnergyFromSegments(
                                        segments,
                                        dailyPeak,
                                        dailyOffPeak,
                                        dailyShoulder
                                      )
                                    : {
                                        peak:
                                          (last(segments) as { peak?: number } | undefined)?.peak ?? refP?.peak ?? 0,
                                        offPeak:
                                          (last(segments) as { offPeak?: number } | undefined)?.offPeak ??
                                          refP?.offPeak ??
                                          0,
                                        shoulder:
                                          (last(segments) as { shoulder?: number } | undefined)?.shoulder ??
                                          refP?.shoulder ??
                                          0
                                      };
                                  return (
                                    <>
                                      <Chip
                                        sx={ChipMarginBottom}
                                        size="small"
                                        avatar={<Avatar>P</Avatar>}
                                        label={formatNumber(weightedPeakRate, 4, '-')}
                                      />
                                      <br />
                                      <Chip
                                        sx={ChipMarginBottom}
                                        size="small"
                                        avatar={<Avatar>OP</Avatar>}
                                        label={formatNumber(weightedOffPeakRate, 4, '-')}
                                      />
                                      <br />
                                      <Chip
                                        size="small"
                                        avatar={<Avatar>S</Avatar>}
                                        label={formatNumber(weightedShoulderRate, 4, '-')}
                                      />
                                    </>
                                  );
                                })()}
                              </StyledTableCell>
                              <StyledTableCell>${formatNumber(energyCost * divide, 0, '-')}</StyledTableCell>
                              <StyledTableCell>
                                ${formatNumber(sum([renewableCost, retailerFee, meteringCharge]) * divide, 0, '-')}
                              </StyledTableCell>
                              <StyledTableCell>${formatNumber(contestableCost * divide, 0, '-')}</StyledTableCell>
                              <StyledTableCell>
                                ${formatNumber(sum([networkCharge, regulatedCost]) * divide, 0, '-')}
                              </StyledTableCell>
                              <StyledTableCell>
                                $
                                {formatNumber(
                                  sum([
                                    energyCost,
                                    regulatedCost,
                                    renewableCost,
                                    retailerFee,
                                    meteringCharge,
                                    networkCharge
                                  ]) * divide,
                                  0,
                                  '-'
                                )}
                              </StyledTableCell>
                              {hasCurrentContract && (
                                <>
                                  <StyledTableCell
                                    sx={{
                                      bgcolor:
                                        sum([
                                          energyCost,
                                          regulatedCost,
                                          renewableCost,
                                          retailerFee,
                                          meteringCharge,
                                          networkCharge
                                        ]) *
                                          divide >
                                        sum([
                                          meter.annualData.contestableCost,
                                          meter.annualData.networkStanding,
                                          meter.annualData.networkChargesEnergy,
                                          meter.annualData.networkChargesDemand,
                                          meter.annualData.regulatedCost
                                        ])
                                          ? '#ef9a9a'
                                          : '#a5d6a7',
                                      color:
                                        sum([
                                          energyCost,
                                          regulatedCost,
                                          renewableCost,
                                          retailerFee,
                                          meteringCharge,
                                          networkCharge
                                        ]) *
                                          divide >
                                        sum([
                                          meter.annualData.contestableCost,
                                          meter.annualData.networkStanding,
                                          meter.annualData.networkChargesEnergy,
                                          meter.annualData.networkChargesDemand,
                                          meter.annualData.regulatedCost
                                        ])
                                          ? '#c62828'
                                          : '#1b5e20'
                                    }}
                                  >
                                    $
                                    {formatNumber(
                                      sum([
                                        energyCost,
                                        regulatedCost,
                                        renewableCost,
                                        retailerFee,
                                        meteringCharge,
                                        networkCharge
                                      ]) *
                                        divide -
                                        sum([
                                          meter.annualData.contestableCost,
                                          meter.annualData.networkStanding,
                                          meter.annualData.networkChargesEnergy,
                                          meter.annualData.networkChargesDemand,
                                          meter.annualData.regulatedCost
                                        ]),
                                      0,
                                      '-'
                                    )}
                                  </StyledTableCell>
                                  <StyledTableCell
                                    sx={{
                                      bgcolor:
                                        sum([
                                          energyCost,
                                          regulatedCost,
                                          renewableCost,
                                          retailerFee,
                                          meteringCharge,
                                          networkCharge
                                        ]) *
                                          divide >
                                        sum([
                                          meter.annualData.contestableCost,
                                          meter.annualData.networkStanding,
                                          meter.annualData.networkChargesEnergy,
                                          meter.annualData.networkChargesDemand,
                                          meter.annualData.regulatedCost
                                        ])
                                          ? '#ef9a9a'
                                          : '#a5d6a7',
                                      color:
                                        sum([
                                          energyCost,
                                          regulatedCost,
                                          renewableCost,
                                          retailerFee,
                                          meteringCharge,
                                          networkCharge
                                        ]) *
                                          divide >
                                        sum([
                                          meter.annualData.contestableCost,
                                          meter.annualData.networkStanding,
                                          meter.annualData.networkChargesEnergy,
                                          meter.annualData.networkChargesDemand,
                                          meter.annualData.regulatedCost
                                        ])
                                          ? '#c62828'
                                          : '#1b5e20'
                                    }}
                                  >
                                    {formatNumber(
                                      ((sum([
                                        energyCost,
                                        regulatedCost,
                                        renewableCost,
                                        retailerFee,
                                        meteringCharge,
                                        networkCharge
                                      ]) *
                                        divide -
                                        sum([
                                          meter.annualData.contestableCost,
                                          meter.annualData.networkStanding,
                                          meter.annualData.networkChargesEnergy,
                                          meter.annualData.networkChargesDemand,
                                          meter.annualData.regulatedCost
                                        ])) /
                                        sum([
                                          meter.annualData.contestableCost,
                                          meter.annualData.networkStanding,
                                          meter.annualData.networkChargesEnergy,
                                          meter.annualData.networkChargesDemand,
                                          meter.annualData.regulatedCost
                                        ])) *
                                        100,
                                      2,
                                      '-'
                                    )}
                                    %
                                  </StyledTableCell>
                                </>
                              )}
                            </StyledTableRow>
                          );
                        })}
                      </React.Fragment>
                    );
                  }
                )}

                <TableRow sx={TotalRowStyle}>
                  <StyledTableCell colSpan={5}></StyledTableCell>
                  <StyledTableCell sx={TotalLabelCellStyle}>Total</StyledTableCell>
                  <StyledTableCell sx={TotalValueCellStyle}>
                    ${formatNumber(totalEnergyCostAnnual, 0, '-')}
                  </StyledTableCell>
                  <StyledTableCell sx={TotalValueCellStyle}>
                    ${formatNumber(totalEnvRetailerAnnual, 0, '-')}
                  </StyledTableCell>
                  <StyledTableCell sx={TotalValueCellStyle}>
                    ${formatNumber(totalContestableAnnual, 0, '-')}
                  </StyledTableCell>
                  <StyledTableCell sx={TotalValueCellStyle}>
                    ${formatNumber(totalNetworkRegulatedAnnual, 0, '-')}
                  </StyledTableCell>
                  <StyledTableCell sx={TotalValueCellStyle}>
                    ${formatNumber(totalNewExpenditureAnnual, 0, '-')}
                  </StyledTableCell>
                  {hasCurrentContract && (
                    <>
                      <StyledTableCell
                        sx={{
                          fontWeight: 'bold',
                          bgcolor: totalNewExpenditureAnnual > totalCurrentExpenditure ? '#ef9a9a' : '#a5d6a7',
                          color: totalNewExpenditureAnnual > totalCurrentExpenditure ? '#c62828' : '#1b5e20'
                        }}
                      >
                        ${formatNumber(totalNewExpenditureAnnual - totalCurrentExpenditure, 0, '-')}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{
                          fontWeight: 'bold',
                          bgcolor: totalNewExpenditureAnnual > totalCurrentExpenditure ? '#ef9a9a' : '#a5d6a7',
                          color: totalNewExpenditureAnnual > totalCurrentExpenditure ? '#c62828' : '#1b5e20'
                        }}
                      >
                        {formatNumber(
                          totalCurrentExpenditure > 0
                            ? ((totalNewExpenditureAnnual - totalCurrentExpenditure) / totalCurrentExpenditure) * 100
                            : 0,
                          2,
                          '-'
                        )}
                        %
                      </StyledTableCell>
                    </>
                  )}
                </TableRow>
              </TableBody>
            </StyledTable>
          </TableContainer>
        </SectionContainer>
      </Section>
    </>
  );
};

export default SummarySection;
